import { virtualRoomAreaService, VirtualRoomArea, VirtualRoomAreaEvent } from './VirtualRoomAreaService'
import { phaserEvents, Event } from '../events/EventCenter'
import phaserGame from '../PhaserGame'
import Game from '../scenes/Game'

/**
 * Player Area State
 */
export interface PlayerAreaState {
  playerId: string
  currentArea: VirtualRoomArea | null
  previousArea: VirtualRoomArea | null
  enteredAt: number | null
  lastCheckTime: number
  lastPosition: { x: number; y: number } | null
}

/**
 * Area Detection Event Data
 */
export interface AreaDetectionEvent {
  playerId: string
  area: VirtualRoomArea | null
  previousArea: VirtualRoomArea | null
  eventType: 'enter' | 'leave' | 'change'
  timestamp: number
}

/**
 * Virtual Room Area Detection Service
 * 
 * This service provides modular detection for when players enter and leave virtual room areas.
 * It works for both MyPlayer and OtherPlayer instances.
 */
export class VirtualRoomAreaDetectionService {
  private static instance: VirtualRoomAreaDetectionService | null = null
  
  // Player area states
  private playerStates: Map<string, PlayerAreaState> = new Map()
  
  // Configuration
  private config = {
    enableLogging: true,
    enableEvents: true,
    detectionInterval: 100, // Reduced from default for faster responsiveness
    positionThreshold: 5, // Reduced threshold for more sensitive detection
  }
  
  // Detection timer
  private detectionTimer: NodeJS.Timeout | null = null
  
  // Event listeners
  private eventListeners: Map<string, Set<Function>> = new Map()

  /**
   * Get singleton instance
   */
  public static getInstance(): VirtualRoomAreaDetectionService {
    if (!VirtualRoomAreaDetectionService.instance) {
      VirtualRoomAreaDetectionService.instance = new VirtualRoomAreaDetectionService()
    }
    return VirtualRoomAreaDetectionService.instance
  }

  /**
   * Initialize the detection service
   */
  public initialize(): void {
    console.log('[VirtualRoomAreaDetectionService] Initializing detection service')
    
    // Start detection timer
    this.startDetectionTimer()
    
    // Listen for area service events
    virtualRoomAreaService.on(VirtualRoomAreaEvent.AREAS_LOADED, this.handleAreasLoaded.bind(this))
  }

  /**
   * Register a player for area detection
   */
  public registerPlayer(playerId: string, initialX: number, initialY: number): void {
    if (this.playerStates.has(playerId)) {
      console.warn(`[VirtualRoomAreaDetectionService] Player ${playerId} already registered`)
      return
    }

    const initialState: PlayerAreaState = {
      playerId,
      currentArea: null,
      previousArea: null,
      enteredAt: null,
      lastCheckTime: Date.now(),
      lastPosition: null
    }

    this.playerStates.set(playerId, initialState)
    
    // Perform initial area check
    this.checkPlayerArea(playerId, initialX, initialY)
    
    console.log(`[VirtualRoomAreaDetectionService] Registered player ${playerId} for area detection`)
  }

  /**
   * Unregister a player from area detection
   * Prevents double-unregistration by checking if player exists first
   */
  public unregisterPlayer(playerId: string): void {
    const state = this.playerStates.get(playerId)
    if (!state) {
      // Silently return - player may have already been unregistered
      return
    }

    // Emit leave event if player was in an area
    if (state.currentArea) {
      this.emitAreaEvent({
        playerId,
        area: null,
        previousArea: state.currentArea,
        eventType: 'leave',
        timestamp: Date.now()
      })
    }

    this.playerStates.delete(playerId)
    if (this.config.enableLogging) {
      console.log(`[VirtualRoomAreaDetectionService] Unregistered player ${playerId} from area detection`)
    }
  }

  /**
   * Check if a player is registered
   */
  public isPlayerRegistered(playerId: string): boolean {
    return this.playerStates.has(playerId)
  }

  /**
   * Update player position and check for area changes
   * Silently handles unregistered players to prevent console spam
   */
  public updatePlayerPosition(playerId: string, x: number, y: number): void {
    const state = this.playerStates.get(playerId)
    if (!state) {
      // Silently return - player may have been unregistered during cleanup
      return
    }

    // Throttle position updates - only update if position changed significantly
    const positionThreshold = this.config.positionThreshold
    if (state.lastPosition) {
      const dx = Math.abs(state.lastPosition.x - x)
      const dy = Math.abs(state.lastPosition.y - y)
      
      // Skip update if position hasn't changed significantly
      if (dx < positionThreshold && dy < positionThreshold) {
        return
      }
    }

    // Update position and immediately check area
    state.lastPosition = { x, y }
    this.checkPlayerArea(playerId, x, y)
  }

  /**
   * Get current area for a player
   */
  public getPlayerCurrentArea(playerId: string): VirtualRoomArea | null {
    const state = this.playerStates.get(playerId)
    return state?.currentArea || null
  }

  /**
   * Get player area state
   */
  public getPlayerAreaState(playerId: string): PlayerAreaState | null {
    return this.playerStates.get(playerId) || null
  }

  /**
   * Add event listener for area detection events
   */
  public on(eventType: string, callback: Function): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set())
    }
    this.eventListeners.get(eventType)!.add(callback)
  }

  /**
   * Remove event listener
   */
  public off(eventType: string, callback: Function): void {
    const listeners = this.eventListeners.get(eventType)
    if (listeners) {
      listeners.delete(callback)
    }
  }

  /**
   * Check if a player is in a specific area
   */
  public isPlayerInArea(playerId: string, areaId: string): boolean {
    const state = this.playerStates.get(playerId)
    return state?.currentArea?.roomId === areaId
  }

  /**
   * Get all players in a specific area
   */
  public getPlayersInArea(areaId: string): string[] {
    const players: string[] = []
    
    for (const [playerId, state] of this.playerStates) {
      if (state.currentArea?.roomId === areaId) {
        players.push(playerId)
      }
    }
    
    return players
  }

  /**
   * Get all areas with player counts
   */
  public getAreaPlayerCounts(): Map<string, number> {
    const areaCounts = new Map<string, number>()
    
    for (const state of this.playerStates.values()) {
      if (state.currentArea) {
        const currentCount = areaCounts.get(state.currentArea.roomId) || 0
        areaCounts.set(state.currentArea.roomId, currentCount + 1)
      }
    }
    
    return areaCounts
  }

  private _destroyed = false

  /**
   * Destroy the service. Safe to call multiple times (idempotent).
   */
  public destroy(): void {
    if (this._destroyed) return
    this._destroyed = true

    console.log('[VirtualRoomAreaDetectionService] Destroying detection service')
    
    // Stop detection timer
    this.stopDetectionTimer()
    
    // Clear all player states
    this.playerStates.clear()
    
    // Clear event listeners
    this.eventListeners.clear()
    
    // Clear instance
    VirtualRoomAreaDetectionService.instance = null
  }

  // Private methods

  /**
   * Start the detection timer
   */
  private startDetectionTimer(): void {
    if (this.detectionTimer) {
      clearInterval(this.detectionTimer)
    }

    this.detectionTimer = setInterval(() => {
      this.performPeriodicChecks()
    }, this.config.detectionInterval)
    
    console.log(`[VirtualRoomAreaDetectionService] Started detection timer (${this.config.detectionInterval}ms interval)`)
  }

  /**
   * Stop the detection timer
   */
  private stopDetectionTimer(): void {
    if (this.detectionTimer) {
      clearInterval(this.detectionTimer)
      this.detectionTimer = null
      console.log('[VirtualRoomAreaDetectionService] Stopped detection timer')
    }
  }

  /**
   * Perform periodic checks for all registered players
   */
  private performPeriodicChecks(): void {
    // This method can be used for additional periodic checks if needed
    // Currently, area detection is done on position updates for better performance
  }

  /**
   * Check if a player is in an area using footprint-based detection with hysteresis
   * 
   * This method uses the enhanced isPlayerInArea which:
   * - Checks the avatar's floor footprint instead of just the center point
   * - Applies tolerance margins to prevent flickering at boundaries
   * - Uses hysteresis to maintain membership when previously in an area
   */
  private checkPlayerArea(playerId: string, x: number, y: number): void {
    const state = this.playerStates.get(playerId)
    if (!state) return

    // Convert to tile coordinates for logging
    const tileX = Math.floor(x / 32)
    const tileY = Math.floor(y / 32)

    // Check if player is in any area using footprint-based detection
    // Pass previous area for hysteresis (prevents flickering at boundaries)
    const currentArea = virtualRoomAreaService.isPlayerInArea(x, y, undefined, state.currentArea)
    
    // Check if area changed
    if (currentArea?.roomId !== state.currentArea?.roomId) {
      const previousArea = state.currentArea
      const timestamp = Date.now()

      // Update state
      state.previousArea = state.currentArea
      state.currentArea = currentArea
      state.enteredAt = currentArea ? timestamp : null
      state.lastCheckTime = timestamp

      // Determine event type
      let eventType: 'enter' | 'leave' | 'change' = 'change'
      if (!previousArea && currentArea) {
        eventType = 'enter'
      } else if (previousArea && !currentArea) {
        eventType = 'leave'
      }

      // Emit event
      this.emitAreaEvent({
        playerId,
        area: currentArea,
        previousArea,
        eventType,
        timestamp
      })

      // Log the change
      if (this.config.enableLogging) {
        if (eventType === 'enter') {
          console.log(`[VirtualRoomAreaDetectionService] Player ${playerId} entered area: "${currentArea?.roomName}" (${tileX}, ${tileY})`)
        } else if (eventType === 'leave') {
          console.log(`[VirtualRoomAreaDetectionService] Player ${playerId} left area: "${previousArea?.roomName}" (${tileX}, ${tileY})`)
        } else {
          console.log(`[VirtualRoomAreaDetectionService] Player ${playerId} changed from "${previousArea?.roomName}" to "${currentArea?.roomName}" (${tileX}, ${tileY})`)
        }
      }
    } else {
      // Update last check time even if no change
      state.lastCheckTime = Date.now()
    }
  }

  /**
   * Handle areas loaded event
   */
  private handleAreasLoaded(data: { customRoomId: string; areas: VirtualRoomArea[] }): void {
    // Immediately re-check all players with new areas instead of waiting
    for (const [playerId, state] of this.playerStates) {
      // Get current position from the game if available
      const game = phaserGame?.scene?.keys?.game as Game;
      if (game) {
        if (playerId === game.myPlayer?.playerId) {
          // Re-check local player
          this.checkPlayerArea(playerId, game.myPlayer.x, game.myPlayer.y);
        } else {
          // Re-check other players
          const otherPlayer = game.getOtherPlayerById(playerId);
          if (otherPlayer) {
            this.checkPlayerArea(playerId, otherPlayer.x, otherPlayer.y);
          }
        }
      }
    }
  }

  /**
   * Emit area detection event
   */
  private emitAreaEvent(event: AreaDetectionEvent): void {
    if (!this.config.enableEvents) return

    // Emit to local listeners
    const listeners = this.eventListeners.get(event.eventType)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(event)
        } catch (error) {
          console.error(`[VirtualRoomAreaDetectionService] Error in event listener:`, error)
        }
      })
    }

    // Emit to Phaser events
    switch (event.eventType) {
      case 'enter':
        phaserEvents.emit(Event.PLAYER_ENTERED_VIRTUAL_ROOM, event)
        break
      case 'leave':
        phaserEvents.emit(Event.PLAYER_LEFT_VIRTUAL_ROOM, event)
        break
      case 'change':
        phaserEvents.emit(Event.PLAYER_CHANGED_VIRTUAL_ROOM, event)
        break
    }
  }
}

// Export singleton instance
export const virtualRoomAreaDetectionService = VirtualRoomAreaDetectionService.getInstance() 